import { useEffect, useState } from 'react'

import {
	EuiButton,
	EuiCallOut,
	EuiFieldText,
	EuiFlexGroup,
	EuiFlexItem,
	EuiForm,
	EuiFormRow,
	EuiHorizontalRule,
	EuiPanel,
	EuiSpacer,
} from '@equipmentshare/ds2'

import InventoryLocationPicker from 'components/InventoryLocationPicker'
import ItemRow from '../ItemRow'
import ReasonSelect from '../ReasonSelect'

import { InventoryLocation } from 'inventory-web'
import { IncrementOrDecrement, ManualAdjustmentReasonType, Part, Permission } from '../../../../graphql'
import useCreateManualAdjustment from '../../effects/createManualAdjustment'
import validateForm, { SetUnexpectedCost } from './helpers/validateForm'
import useCurrentStore from 'views/hooks/useCurrentStore'
import { heapEvent } from '@equipmentshare/heaper'

export type UnexpectedItemCost = {
	[ key: number ]: 'high' | 'low',
}

export type AddToInventoryFormState = {
	inventoryLocation?: InventoryLocation,
	reason?: ManualAdjustmentReasonType,
	notes: string,
	validation: {
		inventoryLocation: { isInvalid: boolean, error?: string },
		reason: { isInvalid: boolean, error?: string },
		items: { isInvalid: boolean, error?: string },
	},
}

export type AddToInventoryItemFormState = {
	key: number,
	item?: Part,
	quantity: string,
	cost: string,
	validation: {
		item: { isInvalid: boolean, error?: string },
		quantity: { isInvalid: boolean, error?: string },
		cost: { isInvalid: boolean, error?: string },
	},
}

export type AddToInventoryFormProps = {
	goBack: () => void,
}

export default function AddToInventoryForm( { goBack }: AddToInventoryFormProps ) {
	const [ formState, setFormState ] = useState<AddToInventoryFormState>( {
		notes: '',
		validation: {
			inventoryLocation: { isInvalid: false },
			reason: { isInvalid: false },
			items: { isInvalid: false }
		}
	} )

	const [ formItems, setFormItems ] = useState<AddToInventoryItemFormState[]>( [ {
		key: 0,
		quantity: '',
		cost: '',
		validation: {
			item: { isInvalid: false },
			quantity: { isInvalid: false },
			cost: { isInvalid: false },
		}
	} ] )

	const [ nextItemKey, setNextItemKey ] = useState( 1 )
	const [ unexpectedItemCost, setUnexpectedItemCost ] = useState<UnexpectedItemCost>( {} )
	const { createManualAdjustment, createManualAdjustmentState } = useCreateManualAdjustment()
	const { currentInventoryLocation, loading: isCurrentStoreLoading } = useCurrentStore()

	useEffect( () => {
		if ( currentInventoryLocation ) {
			setFormState( {
				...formState,
				inventoryLocation: currentInventoryLocation,
				validation: {
					...formState.validation,
					inventoryLocation: { isInvalid: false },
				}
			} )
		}
	}, [ currentInventoryLocation ] )

	const onChangeInventoryLocation = ( _: number, inventoryLocation?: InventoryLocation ) => setFormState( {
		...formState,
		inventoryLocation,
		validation: {
			...formState.validation,
			inventoryLocation: { isInvalid: false },
		}
	} )

	const onChangeNotes = ( event: React.ChangeEvent<HTMLInputElement> ) => setFormState( {
		...formState,
		notes: event.currentTarget.value,
	} )
	const onChangeReason = ( reason?: ManualAdjustmentReasonType ) => setFormState( {
		...formState,
		reason,
		validation: {
			...formState.validation,
			reason: { isInvalid: false },
		}
	} )

	const setItemFormState = ( rowKey: number ) => ( itemFormState: AddToInventoryItemFormState ) => {
		setFormItems( formItems.map( ( currentFormItem ) => {
			if ( currentFormItem.key === rowKey ) {
				return itemFormState
			}
			return currentFormItem
		} ) )
	}

	const setUnexpectedCost: SetUnexpectedCost = ( key, flag ) => {
		if ( flag === 'normal' ) {
			// remove from the list
			const newUnexpectedCost = { ...unexpectedItemCost }
			delete newUnexpectedCost[ key ]
			setUnexpectedItemCost( newUnexpectedCost )
		} else {
			setUnexpectedItemCost( {
				...unexpectedItemCost,
				[ key ]: flag,
			} )
		}
	}

	const ignoreUnexpectedCost = () => {
		setUnexpectedItemCost( {} )
		setFormItems( formItems.map( ( item ) => (
			{
				...item,
				validation: {
					...item.validation,
					cost: { isInvalid: false },
				}
			}
		) ) )
	}

	const onAddItemClick = () => {
		setFormItems( [
			...formItems,
			{
				key: nextItemKey,
				quantity: '',
				cost: '',
				validation: {
					item: { isInvalid: false },
					quantity: { isInvalid: false },
					cost: { isInvalid: false },
				},
			},
		] )
		setNextItemKey( nextItemKey + 1 )
		setFormState( {
			...formState,
			validation: {
				...formState.validation,
				items: { isInvalid: false },
			}
		} )
	}

	const onRemoveItemClick = ( rowKey: number ) => () => {
		setFormItems( formItems.filter( ( formItem ) => formItem.key !== rowKey ) )
	}


	const onSubmitClick = async() => {
		if ( Object.keys( unexpectedItemCost ).length === 0 ) {
			heapEvent( 'Inventory - Add to Inventory - Click - Submit', {} )
			const { newFormState, newItems, isInvalid } = validateForm( formState, formItems )

			if ( !isInvalid ) {
				await createManualAdjustment(
					formState.inventoryLocation?.id ?? '',
					formState.reason as ManualAdjustmentReasonType,
					formItems.map( ( formItem ) => ( {
						productId: formItem.item?.id?.toString() ?? '',
						quantity: parseInt( formItem.quantity, 10 ),
						costPerItem: parseFloat( formItem.cost ),
					} ) ),
					IncrementOrDecrement.Increment,
					formState.notes,
				)
				goBack()
			} else {
				setFormState( newFormState )
				setFormItems( newItems )
			}
		}
	}

	const onCancelClick = () => {
		heapEvent( 'Inventory - Add to Inventory - Click - Cancel', {} )
		goBack()
	}

	const storeId = formState.inventoryLocation?.id ? parseInt( formState.inventoryLocation.id, 10 ) : undefined
	const branchId = formState.inventoryLocation?.branchId
	const isSubmitting = createManualAdjustmentState.loading

	return (
		<>
			<EuiPanel hasShadow={false} paddingSize="none" className="eui-yScroll">
				{ Object.keys( unexpectedItemCost ).length > 0 && (
					<>
						<EuiCallOut
							color="danger"
							title="Price Adjustment"
						>
							{ Object.entries( unexpectedItemCost ).map( ( [ key, flag ] ) => {
								const reactKey = `adjust-item-${key}`
								const item = formItems.find( ( value ) => value.key.toString() === key )
								if ( item ) {
									const partNumber = item.item?.partNumber
									const direction = flag === 'high' ? 'higher' : 'lower'
									return (
										<div key={key}>
											The price entered below for item #{partNumber} is {direction} than our recorded price.
										</div>
									)
								}
								return <div key={reactKey} />
							} ) }
							<div>If this is correct, click &lsquo;Ignore&rsquo; below to continue. Otherwise, adjust accordingly.</div>
							<EuiSpacer size="m" />
							<EuiButton
								onClick={ignoreUnexpectedCost}
								color="danger"
								id="ignoreUnexpectedCost"
							>
								Ignore
							</EuiButton>
						</EuiCallOut>
						<EuiSpacer size="m" />
					</>
				) }
				<InventoryLocationPicker
					onChange={onChangeInventoryLocation}
					selectedInventoryLocation={formState.inventoryLocation}
					formRowProps={formState.validation.inventoryLocation}
					isInvalid={formState.validation.inventoryLocation.isInvalid}
					isLoading={isCurrentStoreLoading}
					label={<>Store <span className="required">*</span></>}
					permissions={[ Permission.CanCreateIncrementAdjustmentByStoreId ]}
				/>
				<EuiFormRow
					label={<>Reason <span className="required">*</span></>}
					isInvalid={formState.validation.reason.isInvalid}
					error={formState.validation.reason.error}
				>
					<ReasonSelect
						selectedReason={formState.reason}
						onChange={onChangeReason}
						reasonType={IncrementOrDecrement.Increment}
						isInvalid={formState.validation.reason.isInvalid}
						storeId={storeId}
						branchId={branchId}
					/>
				</EuiFormRow>

				<EuiFormRow label="Notes">
					<EuiFieldText
						value={formState.notes}
						onChange={onChangeNotes}
					/>
				</EuiFormRow>
				<EuiHorizontalRule margin="l" />
				<EuiForm
					isInvalid={formState.validation.items.isInvalid}
					error={formState.validation.items.error}
				>
					{ formItems.map( ( formItem ) => (
						<ItemRow
							key={formItem.key}
							itemFormState={formItem}
							setItemFormState={setItemFormState( formItem.key )}
							onDeleteClick={onRemoveItemClick( formItem.key )}
							storeId={storeId}
							setUnexpectedCost={setUnexpectedCost}
						/>
					) ) }
				</EuiForm>
				<EuiHorizontalRule margin="l" />
				<EuiButton
					id="addItemBtn"
					iconType="plusInCircle"
					onClick={onAddItemClick}
					color="text"
				>
					Add item
				</EuiButton>
				<EuiSpacer size="xxl" />
				<EuiSpacer size="xl" />

			</EuiPanel>
			<EuiFlexGroup>
				<EuiFlexItem grow={false}>
					<EuiButton
						color="text"
						onClick={onCancelClick}
						id="cancelBtn"
					>
						Cancel
					</EuiButton>
				</EuiFlexItem>
				<EuiFlexItem grow={false}>
					<EuiButton
						fill
						onClick={onSubmitClick}
						id="submitBtn"
						disabled={isSubmitting}
					>
						Add to inventory
					</EuiButton>
				</EuiFlexItem>
			</EuiFlexGroup>
		</>
	)
}
